// src/components/layout/filters/TraceabilityFilters.js
import React, { useState } from 'react';
import { Filter, Calendar, Tag } from 'lucide-react';

export const BRAND_OPTIONS = ['BYD', 'BMW', 'JEEP', 'KIA'];

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const YEARS = ['2026', '2025', '2024', '2023'];

const TraceabilityFilters = ({ onChangeFilters }) => {
  const currentDate = new Date();
  const currentYearStr = currentDate.getFullYear().toString();
  const currentMonthIdx = currentDate.getMonth(); // 0-11
  const currentMonthName = MONTHS[currentMonthIdx];

  const prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
  const prevMonthName = MONTHS[prevDate.getMonth()];
  const prevYearStr = prevDate.getFullYear().toString();

  const [selectedBrands, setSelectedBrands] = useState([]);

  // Estados independientes de Año/Mes para Inicio y Fin
  const [startYear, setStartYear] = useState(prevYearStr);
  const [startMonth, setStartMonth] = useState(prevMonthName);

  const [endYear, setEndYear] = useState(currentYearStr);
  const [endMonth, setEndMonth] = useState(currentMonthName);

  const getMonthIndex = (mName) => MONTHS.indexOf(mName);

  const notifyChange = (updates) => {
    // Combinar estado actual con actualizaciones para asegurar valores frescos
    const newState = {
      brands: selectedBrands,
      startYear,
      startMonth,
      endYear,
      endMonth,
      ...updates
    };

    // Siempre emitir las marcas seleccionadas explícitamente, incluso si todas están seleccionadas.
    // Esto elimina la ambigüedad sobre qué significa "vacío" para el backend.
    const brandsToEmit = newState.brands;

    onChangeFilters({
      brands: brandsToEmit,
      startYear: newState.startYear,
      startMonth: newState.startMonth,
      endYear: newState.endYear,
      endMonth: newState.endMonth
    });
  };



  const handleToggleBrand = (brand) => {
    let newBrands;
    if (selectedBrands.includes(brand)) {
      newBrands = selectedBrands.filter(b => b !== brand);
    } else {
      newBrands = [...selectedBrands, brand];
    }
    setSelectedBrands(newBrands);
    notifyChange({ brands: newBrands });
  };

  // Auxiliar para comparar fechas
  const validateDateRange = (sYear, sMonth, eYear, eMonth) => {
    const sIndex = parseInt(sYear) * 12 + getMonthIndex(sMonth);
    const eIndex = parseInt(eYear) * 12 + getMonthIndex(eMonth);
    return sIndex <= eIndex;
  };

  const handleStartYearChange = (e) => {
    const newYear = e.target.value;
    setStartYear(newYear);

    // Auto-corregir Fin si Inicio > Fin
    if (!validateDateRange(newYear, startMonth, endYear, endMonth)) {
      setEndYear(newYear);
      setEndMonth(startMonth);
      notifyChange({ startYear: newYear, endYear: newYear, endMonth: startMonth });
    } else {
      notifyChange({ startYear: newYear });
    }
  };

  const handleStartMonthChange = (e) => {
    const newMonth = e.target.value;
    setStartMonth(newMonth);

    if (!validateDateRange(startYear, newMonth, endYear, endMonth)) {
      setEndMonth(newMonth);
      notifyChange({ startMonth: newMonth, endMonth: newMonth });
    } else {
      notifyChange({ startMonth: newMonth });
    }
  };

  const handleEndYearChange = (e) => {
    const newYear = e.target.value;
    setEndYear(newYear);

    if (!validateDateRange(startYear, startMonth, newYear, endMonth)) {
      setStartYear(newYear);
      setStartMonth(endMonth);
      notifyChange({ endYear: newYear, startYear: newYear, startMonth: endMonth });
    } else {
      notifyChange({ endYear: newYear });
    }
  };

  const handleEndMonthChange = (e) => {
    const newMonth = e.target.value;
    setEndMonth(newMonth);

    if (!validateDateRange(startYear, startMonth, endYear, newMonth)) {
      setStartMonth(newMonth);
      notifyChange({ endMonth: newMonth, startMonth: newMonth });
    } else {
      notifyChange({ endMonth: newMonth });
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
      <h2 className="text-sm font-semibold text-gray-900 mb-3">
        Filtros de trazabilidad
      </h2>

      <div className="flex flex-col gap-4 flex-1">
        {/* Fila 1: Selección de Marca */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
            <Tag className="w-3.5 h-3.5 text-gray-400" />
            Marca
          </label>
          <div className="flex flex-wrap gap-2">
            {BRAND_OPTIONS.map((brand) => {
              const isActive = selectedBrands.includes(brand);
              return (
                <button
                  key={brand}
                  type="button"
                  onClick={() => handleToggleBrand(brand)}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${isActive
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                >
                  {brand}
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-px bg-gray-100 w-full my-1" />

        {/* Fila 2: Filtros de Fecha - Dividido en columnas Desde/Hasta */}
        <div className="grid grid-cols-2 gap-4">

          {/* Columna DESDE */}
          <div className="flex flex-col gap-2">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              Desde
            </label>
            <div className="flex flex-col gap-2">
              {/* Selección de Mes */}
              <select
                value={startMonth}
                onChange={handleStartMonthChange}
                className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2"
              >
                {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              {/* Selección de Año */}
              <select
                value={startYear}
                onChange={handleStartYearChange}
                className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2"
              >
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* Columna HASTA */}
          <div className="flex flex-col gap-2">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              Hasta
            </label>
            <div className="flex flex-col gap-2">
              {/* Selección de Mes */}
              <select
                value={endMonth}
                onChange={handleEndMonthChange}
                className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2"
              >
                {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              {/* Selección de Año */}
              <select
                value={endYear}
                onChange={handleEndYearChange}
                className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2"
              >
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraceabilityFilters;
